import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNavigate } from 'react-router-dom';

import { MaterialIcon, Page } from '@components';
import { routes } from '@routes';
import { useGlobalNotifier } from '@hooks';

import { useCreateStandingAccessReport } from '@StandingAccess/services/standingAccessMutations';
import CreateReportCloudProviderSelector from '@StandingAccess/components/CreateReportCloudProviderSelector';
import CreateReportIntegrationSelector, {
  CreateReportIntegrationItem,
  CreateReportIntegrationSelectorEmptyState,
} from '@StandingAccess/components/CreateReportIntegrationSelector';
import { useCountIntegrationResources } from '@StandingAccess/services/standingAccessQueries';
import { cloudProviders } from '@StandingAccess/types';
import { IntegrationStatus, useListIntegrationsV2 } from '@api';
import { CustomAnalyticsEvents, useAnalyticsContext } from '@utils/analytics';

interface IntegrationItemProps {
  id: string;
  name: string;
  icon: string;
  resourceType: string;
  status: IntegrationStatus;
}

const useFilteredIntegrations = (selectedProvider: string) => {
  const { integrations, configs, isIntegrationsFetched } = useListIntegrationsV2();

  const filteredIntegrations = useMemo(() => {
    if (!integrations || !configs) return [];

    const configsMap = new Map(configs.map((c) => [c.type, c]));
    const cloudProvider = cloudProviders.find((p) => p.type === selectedProvider);

    const list: IntegrationItemProps[] = [];
    integrations.forEach((integration) => {
      const config = configsMap.get(integration.type);
      const isSelectedProvider = integration.type === selectedProvider;

      if (!(cloudProvider && config && isSelectedProvider)) return;

      list.push({
        id: integration.id,
        name: integration.name,
        icon: config.icons.svg,
        resourceType: cloudProvider.resourceType,
        status: integration.status,
      });
    });

    return list;
  }, [integrations, configs, selectedProvider]);

  return { filteredIntegrations, isLoading: !isIntegrationsFetched };
};

function IntegrationItem({ id, name, icon, resourceType, status }: IntegrationItemProps) {
  const { data, isLoading } = useCountIntegrationResources({
    id,
    filter: { resourceType: [resourceType] },
  });

  if (!data || data?.integrationResources.totalCount === 0) return null;

  return (
    <CreateReportIntegrationItem
      id={id}
      name={name}
      icon={icon}
      resourceType={resourceType}
      resourceCount={data?.integrationResources.totalCount}
      isLoading={isLoading}
      status={status}
    />
  );
}

function IntegrationSelector({
  selectedProvider,
  selectedIntegrationId,
  setSelectedIntegrationId,
}: {
  selectedProvider: string;
  selectedIntegrationId: string;
  setSelectedIntegrationId: (value: string) => void;
}) {
  const { filteredIntegrations, isLoading } = useFilteredIntegrations(selectedProvider);

  return (
    <CreateReportIntegrationSelector
      selectedIntegrationId={selectedIntegrationId}
      onIntegrationSelect={setSelectedIntegrationId}
      isLoading={isLoading}
    >
      {filteredIntegrations.length === 0 && <CreateReportIntegrationSelectorEmptyState />}
      {filteredIntegrations.map((integration) => (
        <IntegrationItem key={integration.id} {...integration} />
      ))}
    </CreateReportIntegrationSelector>
  );
}

function CreateReportButton({ integrationId }: { integrationId: string }) {
  const navigate = useNavigate();
  const { notifySuccess } = useGlobalNotifier();
  const { createStandingAccessReport, isLoading, isSuccess } = useCreateStandingAccessReport();

  useEffect(() => {
    if (isSuccess) {
      navigate(routes.StandingAccessReports.path);
      notifySuccess('Report created successfully');
    }
  }, [isSuccess, navigate, notifySuccess]);

  return (
    <Stack direction="row" spacing={2} justifyContent="center">
      <LoadingButton
        variant="contained"
        startIcon={<MaterialIcon symbol="quick_reference_all" />}
        loading={isLoading}
        onClick={() => createStandingAccessReport({ integration_id: integrationId })}
      >
        Assess
      </LoadingButton>
    </Stack>
  );
}

function CreateStandingAccessReportContainer() {
  const { notifySuccess } = useGlobalNotifier();
  const { track } = useAnalyticsContext();

  const [selectedProvider, setSelectedProvider] = useState<string>(cloudProviders[0].type);
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('');

  const handleNotifyMe = (provider: string) => {
    track(CustomAnalyticsEvents.ASSESS_REPORT_NOTIFY_ME_CLICKED, { provider });
    notifySuccess('We will notify you when this assessment is available');
  };

  return (
    <Stack direction="column" spacing={4}>
      <Stack direction="column" spacing={1}>
        <Typography variant="subtitle1">Assess my Environment</Typography>
        <Typography variant="body2" color="text.muted">
          Apono scans your cloud environment to revoke unused standing access and turn standing access into JIT flows.
        </Typography>
      </Stack>
      <CreateReportCloudProviderSelector
        providers={cloudProviders}
        selectedProvider={selectedProvider}
        onProviderSelect={setSelectedProvider}
        onNotifyMe={handleNotifyMe}
      />
      {selectedProvider && (
        <IntegrationSelector
          selectedProvider={selectedProvider}
          selectedIntegrationId={selectedIntegrationId}
          setSelectedIntegrationId={setSelectedIntegrationId}
        />
      )}
      {selectedIntegrationId && <CreateReportButton integrationId={selectedIntegrationId} />}
    </Stack>
  );
}

export default function CreateStandingAccessReportPage() {
  return (
    <Page
      title={routes.CreateStandingAccessReport.label}
      breadcrumbs={[{ label: routes.StandingAccessReports.label, href: routes.StandingAccessReports.path }]}
      contentCentered
      contentPadded
    >
      <CreateStandingAccessReportContainer />
    </Page>
  );
}
